import { useTranslation } from 'react-i18next';
import { Button } from '@monksflow/monks-ui';
import { Check, Loader2, RefreshCw, TriangleAlert } from 'lucide-react';
import { TaxonomyStatusBadge, type TaxonomyStatus } from '@/entities/taxonomy';
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderIdentity,
} from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import { VersionMenu, type VersionMenuProps } from '../VersionMenu/VersionMenu';

export interface TaxonomyDetailHeaderProps {
  schemaName: string;
  status: TaxonomyStatus | null | undefined;
  /** Whether the save-state chip renders at all — only while editing the head draft. */
  canEdit: boolean;
  conflict: string | null;
  saveError: string | null;
  isSaving: boolean;
  onRetrySave: () => void;
  versionMenuProps: VersionMenuProps;
  showUnpublish: boolean;
  onUnpublish: () => void;
  showPublish: boolean;
  onPublish: () => void;
}

export function TaxonomyDetailHeader({
  schemaName,
  status,
  canEdit,
  conflict,
  saveError,
  isSaving,
  onRetrySave,
  versionMenuProps,
  showUnpublish,
  onUnpublish,
  showPublish,
  onPublish,
}: TaxonomyDetailHeaderProps) {
  const { t } = useTranslation('knowledge');

  return (
    <PageHeader>
      <PageHeaderIdentity>
        <PageHeaderBreadcrumb>
          <PageHeaderBreadcrumbLink href={ROUTES.KNOWLEDGE_HUB_TAXONOMY}>
            {t('taxonomy.taxonomyDetail.taxonomyAdministration')}
          </PageHeaderBreadcrumbLink>
          <PageHeaderBreadcrumbPage>{schemaName}</PageHeaderBreadcrumbPage>
        </PageHeaderBreadcrumb>
        <TaxonomyStatusBadge status={status} />
      </PageHeaderIdentity>
      <PageHeaderActions>
        {canEdit && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {/* Never claim "saved" while a save has been rejected — that
                exact lie is what let concurrent edits disappear quietly. */}
            {conflict ? (
              <span className="flex items-center gap-1.5 font-medium text-destructive">
                <TriangleAlert className="size-3.5" />
                {t('taxonomy.taxonomyDetail.notSaved')}
              </span>
            ) : saveError ? (
              /* Checked BEFORE the pending branch: `updateDraft.isPending`
                 is still true on the way out of a failed save, so the
                 spinner would win and claim a request is in flight.
                 The chip IS the affordance — a 409 gets a banner because
                 it needs explaining and its action discards work; this
                 needs neither. */
              <button
                type="button"
                onClick={onRetrySave}
                className="flex items-center gap-1.5 font-medium text-destructive underline-offset-2 hover:underline"
              >
                <TriangleAlert className="size-3.5" />
                {t('taxonomy.taxonomyDetail.notSaved')}
                <span aria-hidden>·</span>
                <RefreshCw className="size-3.5" />
                {t('taxonomy.taxonomyDetail.retrySave')}
              </button>
            ) : isSaving ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                {t('taxonomy.taxonomyDetail.saving')}
              </>
            ) : (
              <>
                <Check className="size-3.5" />
                {t('taxonomy.taxonomyDetail.allChangesSaved')}
              </>
            )}
          </span>
        )}
        <VersionMenu {...versionMenuProps} />
        {showUnpublish && (
          <Button variant="outline" size="default" onClick={onUnpublish}>
            {t('taxonomy.taxonomyDetail.unpublish')}
          </Button>
        )}
        {showPublish && (
          <Button size="default" onClick={onPublish}>
            {t('taxonomy.taxonomyDetail.publish')}
          </Button>
        )}
      </PageHeaderActions>
    </PageHeader>
  );
}

TaxonomyDetailHeader.displayName = 'TaxonomyDetailHeader';
